import React from 'react';
import { Box, IconButton } from '@mui/material';
import Tooltip from '../LibraryTooltip';
import {
  MenuBookOutlined as KnowledgeIcon,
  AutoAwesomeOutlined as AiStarIcon,
  SaveOutlined as SaveIcon,
} from '@mui/icons-material';
import { colors } from '../../../designTokens';

/** Frosted chip on ASSISTANT_THREAD_BG chrome — shared by group containers. */
export const EXECUTE_CHROME_GLOSSY_SURFACE = {
  bgcolor: 'rgba(255, 255, 255, 0.45)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.58)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.72), 0 1px 2px rgba(15, 23, 42, 0.05)',
};

export const EXECUTE_CHROME_GLOSSY_SURFACE_HOVER = {
  bgcolor: 'rgba(255, 255, 255, 0.62)',
  borderColor: 'rgba(255, 255, 255, 0.78)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.85), 0 1px 3px rgba(15, 23, 42, 0.06)',
};

export function executeChromeGroupSx(extra = {}) {
  return {
    ...EXECUTE_CHROME_GLOSSY_SURFACE,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 34,
    height: 34,
    flexShrink: 0,
    overflow: 'hidden',
    maxWidth: '100%',
    ...extra,
  };
}

export function ExecuteChromeGroup({ children, sx }) {
  return (
    <Box sx={{ ...executeChromeGroupSx(), ...sx }}>
      {children}
    </Box>
  );
}

export function ExecuteChromeGroupDivider() {
  return (
    <Box
      sx={{
        width: '1px',
        alignSelf: 'stretch',
        my: 0.45,
        bgcolor: 'rgba(15, 23, 42, 0.1)',
        flexShrink: 0,
      }}
    />
  );
}

export function executeChromeIconButtonSx(active = false) {
  return {
    width: 34,
    height: 34,
    border: '1px solid',
    borderColor: active ? 'rgba(29, 78, 216, 0.38)' : 'rgba(255, 255, 255, 0.58)',
    bgcolor: active ? 'rgba(29, 78, 216, 0.12)' : EXECUTE_CHROME_GLOSSY_SURFACE.bgcolor,
    backdropFilter: EXECUTE_CHROME_GLOSSY_SURFACE.backdropFilter,
    WebkitBackdropFilter: EXECUTE_CHROME_GLOSSY_SURFACE.WebkitBackdropFilter,
    boxShadow: active
      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.55), 0 1px 2px rgba(29, 78, 216, 0.08)'
      : EXECUTE_CHROME_GLOSSY_SURFACE.boxShadow,
    color: active ? 'primary.main' : colors.textSecondary,
    '&:hover': {
      bgcolor: active ? 'rgba(29, 78, 216, 0.16)' : EXECUTE_CHROME_GLOSSY_SURFACE_HOVER.bgcolor,
      color: active ? 'primary.main' : colors.textPrimary,
      borderColor: active ? 'rgba(29, 78, 216, 0.48)' : EXECUTE_CHROME_GLOSSY_SURFACE_HOVER.borderColor,
      boxShadow: active
        ? 'inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 1px 3px rgba(29, 78, 216, 0.1)'
        : EXECUTE_CHROME_GLOSSY_SURFACE_HOVER.boxShadow,
    },
  };
}

export function executeChromeIconButtonInGroupSx(active = false) {
  return {
    width: 34,
    height: 34,
    minWidth: 34,
    border: 'none',
    borderRadius: 0,
    bgcolor: active ? 'rgba(29, 78, 216, 0.1)' : 'transparent',
    boxShadow: 'none',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    color: active ? 'primary.main' : colors.textSecondary,
    '&:hover': {
      bgcolor: active ? 'rgba(29, 78, 216, 0.14)' : 'rgba(15, 23, 42, 0.06)',
      color: active ? 'primary.main' : colors.textPrimary,
    },
  };
}

export function executeChromeGlossyPillSx(extra = {}) {
  return executeChromeGroupSx(extra);
}

export function KnowledgeShelfToggleButton({
  active = false,
  onClick,
  title,
  size = 'small',
  inGroup = false,
}) {
  const label = title || (active ? 'Hide knowledge' : 'Show knowledge');
  return (
    <Tooltip title={label}>
      <IconButton
        size={size}
        onClick={onClick}
        aria-label={label}
        sx={inGroup ? executeChromeIconButtonInGroupSx(active) : executeChromeIconButtonSx(active)}
      >
        <KnowledgeIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Tooltip>
  );
}

export function CreateCapabilityFromChatButton({
  onClick,
  title = 'Create capability from chat',
  inGroup = false,
}) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={title}
        sx={inGroup ? executeChromeIconButtonInGroupSx() : executeChromeIconButtonSx()}
      >
        <AiStarIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Tooltip>
  );
}

export function SaveWorkspaceIconButton({ onClick, title = 'Save workspace' }) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={title}
        sx={executeChromeIconButtonSx()}
      >
        <SaveIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Tooltip>
  );
}
